import {
  Focusable,
  OverlayArrow as OverlayArrowBase,
  Tooltip as TooltipBase,
  TooltipTrigger,
  type TooltipProps,
  type TooltipTriggerComponentProps,
} from "react-aria-components";
import { css, cva, cx } from "../../../../styled-system/css";
import type { SystemStyleObject } from "../../../../styled-system/types";
import { Button } from "./Button";
import { FaCircleInfo } from "react-icons/fa6";
import { type ReactNode, type ReactElement, type ComponentProps } from "react";

export const HoverInfo = ({
  children,
  iconCss,
  icon = <FaCircleInfo />,
  ...props
}: TooltipTriggerComponentProps & { iconCss?: SystemStyleObject; icon?: ReactElement }) => {
  return (
    <TooltipTrigger delay={0} closeDelay={0} {...props}>
      <Button
        variant="contentOnly"
        css={[{ cursor: "help", _hover: { cursor: "auto", backgroundColor: "transparent" } }, iconCss ?? {}]}
        slot={null}
      >
        {icon}
      </Button>
      <Tooltip offset={10}>{children}</Tooltip>
    </TooltipTrigger>
  );
};

export const Tooltip = ({ children, ...props }: TooltipProps) => {
  return (
    <TooltipBase
      {...props}
      className={cx(
        css({
          borderRadius: "light",
          maxWidth: "600px",
          paddingY: "small",
          paddingX: "medium",
          color: "white",
          fontSize: "medium",
          fontWeight: "500",
          backgroundColor: "royalPurple",
        }),
      )}
    >
      {(values) => (
        <>
          <OverlayArrow />
          {typeof children === "function" ? children(values) : children}
        </>
      )}
    </TooltipBase>
  );
};

export function OverlayArrow({ color }: { color?: "white" }) {
  return (
    <OverlayArrowBase
      className={cva({
        base: {
          "& svg": {
            display: "block",
            fill: "royalPurple",
          },
          "&[data-placement=bottom]": {
            marginTop: "28px",
            "& svg": {
              transform: "rotate(180deg)",
            },
          },
          "&[data-placement=left] svg": {
            transform: "rotate(-90deg)",
          },
          "&[data-placement=right] svg": {
            transform: "rotate(90deg)",
          },
        },
        variants: {
          colorScheme: {
            white: {
              "& svg": {
                fill: "white",
                stroke: "grey",
              },
            },
          },
        },
      })({ colorScheme: color })}
    >
      <svg width={8} height={8} viewBox="0 0 8 8">
        <path d="M0 0 L4 4 L8 0" />
      </svg>
    </OverlayArrowBase>
  );
}

export const CustomTriggerTooltip = ({
  children,
  toolTip,
  placement = "top",
  offset = 10,
}: TooltipProps & {
  children: ComponentProps<typeof Focusable>["children"];
  toolTip?: ReactNode;
  placement?: TooltipProps["placement"];
  offset?: number;
}) => {
  if (!toolTip) {
    return <>{children}</>;
  }
  return (
    <TooltipTrigger delay={0}>
      <Focusable>{children}</Focusable>
      <Tooltip placement={placement} offset={offset}>
        {toolTip}
      </Tooltip>
    </TooltipTrigger>
  );
};
